

import React from 'react';
import type { ChargeRaw, AllData, InvoiceSettings } from '../../types';
import Modal from './Modal';
import { PaperAirplaneIcon } from './Icons';
import { generateFeeDetails, processFooterHtml, formatNumber, generateTransferContent } from '../../utils/helpers';

interface NoticePreviewModalProps {
    charge: ChargeRaw;
    onClose: () => void;
    invoiceSettings: InvoiceSettings;
    allData: AllData;
    onSendEmail: (charge: ChargeRaw) => void;
}

const NoticePreviewModal: React.FC<NoticePreviewModalProps> = ({ charge, onClose, invoiceSettings, allData, onSendEmail }) => {
    const feeDetails = generateFeeDetails(charge, allData);

    const paymentContent = generateTransferContent(charge, invoiceSettings);
    const bankShortNameForQR = invoiceSettings.bankName.split(' - ')[0].trim();
    const qrCodeUrl = `https://qr.sepay.vn/img?acc=${invoiceSettings.accountNumber}&bank=${bankShortNameForQR}&amount=${charge.TotalDue}&des=${encodeURIComponent(paymentContent)}`;
    
    const getFooterHtmlForViewer = (settings: InvoiceSettings) => {
        if (!settings.footerShowInViewer || !settings.footerHtml) return '';
        const processedFooter = processFooterHtml(settings.footerHtml);
        const align = settings.footerAlign || 'center';
        const fontSize = { sm: '0.75rem', md: '0.875rem', lg: '1rem' }[settings.footerFontSize || 'sm'];
        return `<div class="text-gray-500" style="text-align: ${align}; font-size: ${fontSize}; margin-top: 1rem; padding-top: 0.5rem; border-top: 1px dashed #ccc;">${processedFooter}</div>`;
    };
    const footerHtml = getFooterHtmlForViewer(invoiceSettings);
    
    const buildingName = (invoiceSettings.buildingName || 'HUD3 LINH ĐÀM').toUpperCase();

    const htmlContent = `
    <div class="font-sans bg-white text-gray-900 p-6 rounded-lg max-w-4xl mx-auto" style="font-family: Arial, sans-serif;">
        <header class="flex justify-between items-start mb-4">
            <div class="flex-1"><img src="${invoiceSettings.logoUrl}" alt="Logo" class="h-16 object-contain"/></div>
            <div class="flex-2 text-center">
                <h1 class="text-xl font-bold uppercase">Phiếu thông báo phí dịch vụ</h1>
                <p>Kỳ: ${charge.Period}</p>
            </div>
            <div class="flex-1 text-right font-semibold text-xs">BAN QUẢN LÝ VẬN HÀNH<br/>NHÀ CHUNG CƯ ${buildingName}</div>
        </header>
        <section class="mb-4 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <p><strong>Căn hộ:</strong> ${charge.UnitID}</p>
            <p><strong>Chủ hộ:</strong> ${charge.OwnerName}</p>
            <p><strong>Diện tích:</strong> ${charge.Area_m2} m²</p>
            <p><strong>SĐT:</strong> ${charge.Phone}</p>
        </section>
        <table class="w-full border-collapse text-sm">
            <thead class="bg-gray-100">
                <tr>
                    <th class="p-2 border text-left font-semibold text-gray-700">Nội dung</th>
                    <th class="p-2 border text-center font-semibold text-gray-700">Số lượng</th>
                    <th class="p-2 border text-right font-semibold text-gray-700">Thành tiền (VND)</th>
                    <th class="p-2 border text-right font-semibold text-gray-700">Thuế GTGT (VND)</th>
                    <th class="p-2 border text-right font-semibold text-gray-700">Tổng cộng (VND)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class="p-2 border text-gray-800">Phí dịch vụ</td>
                    <td class="p-2 border text-center text-gray-800">${charge.Area_m2} m²</td>
                    <td class="p-2 border text-right text-gray-800">${formatNumber(charge.ServiceFee_Base)}</td>
                    <td class="p-2 border text-right text-gray-800">${formatNumber(charge.ServiceFee_VAT)}</td>
                    <td class="p-2 border text-right text-gray-800">${formatNumber(charge.ServiceFee_Total)}</td>
                </tr>
                ${feeDetails.parking.map(item => `
                    <tr>
                        <td class="p-2 border text-gray-800">${item.description}</td>
                        <td class="p-2 border text-center text-gray-800">${item.quantity}</td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(item.base)}</td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(item.vat)}</td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(item.total)}</td>
                    </tr>
                `).join('')}
                ${feeDetails.water.map(item => `
                     <tr>
                        <td class="p-2 border text-gray-800">${item.description}</td>
                        <td class="p-2 border text-center text-gray-800">${item.quantity}</td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(item.base)}</td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(item.vat)}</td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(item.total)}</td>
                    </tr>
                `).join('')}
                 ${feeDetails.adjustments.map(adj => `
                    <tr>
                        <td class="p-2 border text-gray-800">${adj.Description}</td>
                        <td class="p-2 border text-gray-800"></td>
                        <td class="p-2 border text-gray-800"></td>
                        <td class="p-2 border text-gray-800"></td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(adj.Amount)}</td>
                    </tr>
                `).join('')}
                ${feeDetails.other.map(fee => `
                    <tr>
                        <td class="p-2 border text-gray-800">${fee.name}</td>
                        <td class="p-2 border text-gray-800"></td>
                        <td class="p-2 border text-gray-800"></td>
                        <td class="p-2 border text-gray-800"></td>
                        <td class="p-2 border text-right text-gray-800">${formatNumber(fee.amount)}</td>
                    </tr>
                `).join('')}
            </tbody>
            <tfoot>
                <tr class="bg-gray-100 font-bold text-base">
                    <td colspan="4" class="p-2 border text-right text-gray-800">TỔNG CỘNG THANH TOÁN</td>
                    <td class="p-2 border text-right text-red-600">${formatNumber(charge.TotalDue)}</td>
                </tr>
            </tfoot>
        </table>
        <div class="mt-6 flex items-start gap-4">
            <div class="flex-auto">
                <p class="font-bold text-base mb-2">Thông tin thanh toán:</p>
                <table class="text-sm">
                    <tbody>
                        <tr>
                            <td class="font-medium text-gray-600 pr-4 align-top py-0.5">Chủ TK:</td>
                            <td class="font-semibold text-gray-800 py-0.5">${invoiceSettings.accountName}</td>
                        </tr>
                        <tr>
                            <td class="font-medium text-gray-600 pr-4 align-top py-0.5">Số TK:</td>
                            <td class="font-semibold text-gray-800 py-0.5">${invoiceSettings.accountNumber}</td>
                        </tr>
                        <tr>
                            <td class="font-medium text-gray-600 pr-4 align-top py-0.5">Ngân hàng:</td>
                            <td class="font-semibold text-gray-800 py-0.5">${invoiceSettings.bankName}</td>
                        </tr>
                        <tr>
                            <td class="font-medium text-gray-600 pr-4 align-top py-0.5">Nội dung:</td>
                            <td class="font-bold text-black font-mono break-words py-0.5">${paymentContent}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="flex-shrink-0 text-center">
                <img src="${qrCodeUrl}" alt="QR Code" class="w-24 h-24" />
                <p class="text-xs font-medium mt-1">Quét mã để thanh toán</p>
            </div>
        </div>
        ${footerHtml}
    </div>`;

    return (
        <Modal title={`Phiếu báo phí: ${charge.UnitID} - Kỳ ${charge.Period}`} onClose={onClose} size="4xl">
            <>
                <div className="bg-gray-100 -m-6 p-4">
                    <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                </div>
                <div className="flex justify-end gap-3 pt-4 mt-auto border-t">
                    <button
                        onClick={() => onSendEmail(charge)}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-semibold rounded-md shadow-sm hover:bg-primary-focus"
                    >
                        <PaperAirplaneIcon /> Gửi Email
                    </button>
                     <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Đóng
                    </button>
                </div>
            </>
        </Modal>
    );
};

export default NoticePreviewModal;